import { type ModalLayer, toLayerParamKey } from 'src/navigation/reservedParams'
import {
  billPayDetailUrl,
  deactivateBillPayCardUrl,
  deactivateVirtualCardUrl,
  g2ReviewHomeUrl,
  g2ReviewUrl,
  homeUrl,
  profileInfoUrl,
  receiptDeepLinkUrl,
  reportingDraftReportUrl,
  reportingUrl,
  transactionAttachmentUrl,
  transactionDetailUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'

// Overlay URL-route registry (EX-39749 / EX-39786).
//
// The single home for "a dedicated, pretty URL that opens an overlay" — mapping
// a path like `/virtualcards/:id/deactivate/` to its query-param overlay form
// `/virtualcards/:id?modal=deactivate&modalId=:id`. This is how we keep human-
// readable, shareable, public-facing URLs for modals/flyouts while the overlay
// itself is rendered from query params (see `modalParams.ts`); it also preserves
// any pre-existing deep link / `<Lnk>` trigger that still points at the dedicated
// URL. `OverlayRedirects` renders one redirect `<Route>` per entry, and
// `__tests__/overlayRoutes.test.tsx` asserts these paths never collide with the
// flat page route registry.
//
// Data only (no react-router) so the collision test and any tooling can import
// it without pulling routing in; the redirect rendering lives in
// `OverlayRedirects.tsx`.

export type OverlayRoute = {
  // The dedicated overlay URL as a react-router path pattern, e.g.
  // `/virtualcards/:id/deactivate/`. This is the pretty/shareable route that
  // opens the overlay.
  path: string
  // The query-param overlay it maps to: layer (`modal`/`flyout`) + identity.
  layer: ModalLayer
  name: string
  // The real page the overlay renders on top of, built from the matched route
  // params, e.g. `({ id }) => /virtualcards/:id`.
  toPathname: (params: Record<string, string>) => string
  // Maps a matched route param to the overlay's data sub-key. `{ id: 'id' }`
  // turns the route `:id` into the layer's `<layer>Id` param (`modalId`).
  paramMap?: Record<string, string>
}

export const deactivateVirtualCardOverlayRoute: OverlayRoute = {
  path: deactivateVirtualCardUrl(':id'),
  layer: 'modal',
  name: 'deactivate',
  toPathname: ({ id }) => virtualCardDetailUrl(id),
  paramMap: { id: 'id' },
}

export const deactivateBillPayCardOverlayRoute: OverlayRoute = {
  path: deactivateBillPayCardUrl(':id'),
  layer: 'modal',
  name: 'deactivate',
  toPathname: ({ id }) => billPayDetailUrl(id),
  paramMap: { id: 'id' },
}

export const receiptDeepLinkOverlayRoute: OverlayRoute = {
  path: receiptDeepLinkUrl(':id'),
  layer: 'modal',
  name: 'receipt',
  toPathname: ({ id }) => transactionDetailUrl(id),
}

export const transactionAttachmentOverlayRoute: OverlayRoute = {
  path: transactionAttachmentUrl(':id', ':attachmentId'),
  layer: 'modal',
  name: 'receipt',
  toPathname: ({ id }) => transactionDetailUrl(id),
  paramMap: { attachmentId: 'attachmentId' },
}

export const reportingAgentDraftOverlayRoute: OverlayRoute = {
  path: reportingDraftReportUrl(':conversationId'),
  layer: 'flyout',
  name: 'reportingAgent',
  toPathname: () => reportingUrl(),
  paramMap: { conversationId: 'conversationId' },
}

// The G2 review modal has no params: its only state is "open", and the review
// session itself is minted server-side (`useG2TokenMutation`), so nothing about
// it belongs in the URL. What each entry below chooses is the PAGE the overlay
// opens over — which is where the user is left on close.
//
// Three entry points, and picking the wrong one is a UX regression, not a
// style choice:
//
//   1. A trigger INSIDE the React tree (the profile review box, any future
//      in-app CTA) uses NEITHER of these routes. It opens in place via
//      `useModalRoute().getOpenLocation(g2ReviewOverlayRoute.name)`, so the
//      modal renders over the page the user is already on and closing returns
//      them there. A trigger under the routing boundary (`src/feature/`) takes
//      that location from its page as a prop; see `G2ReviewBox`.
//   2. `/profile/info/review` — a shared or emailed link. It has no "current
//      page" to open over, so it lands on the profile page the box lives on.
//   3. `/home/review` — a trigger rendered OUTSIDE the React tree that can only
//      be handed a static URL string, so it cannot call the hook in (1). A
//      Userpilot prompt is the motivating case: its DOM is injected outside
//      React and its CTA typically hard-navigates. Because the overlay's state
//      is entirely in the URL, that full page load still opens the modal.
//
// (3) exists so the overlay name and the `modal*` param convention stay in this
// repo, guarded by the collision test, rather than being hand-written into a
// third-party dashboard where no typecheck or refactor can reach them.
export const g2ReviewOverlayRoute: OverlayRoute = {
  path: g2ReviewUrl(),
  layer: 'modal',
  name: 'g2Review',
  toPathname: () => profileInfoUrl(),
}

export const g2ReviewHomeOverlayRoute: OverlayRoute = {
  path: g2ReviewHomeUrl(),
  layer: 'modal',
  name: g2ReviewOverlayRoute.name,
  toPathname: () => homeUrl(),
}

export const OVERLAY_ROUTES: OverlayRoute[] = [
  deactivateVirtualCardOverlayRoute,
  deactivateBillPayCardOverlayRoute,
  receiptDeepLinkOverlayRoute,
  transactionAttachmentOverlayRoute,
  reportingAgentDraftOverlayRoute,
  g2ReviewOverlayRoute,
  g2ReviewHomeOverlayRoute,
]

/**
 * Build the `{ pathname, search }` an overlay URL route resolves to: the real
 * page (`toPathname`) plus the reserved overlay params
 * (`?<layer>=<name>&<layer><Key>=<value>`). Param keys go through
 * `toLayerParamKey` so the mapping can never drift from the routing hooks.
 */
export const overlayRedirectLocation = (
  route: OverlayRoute,
  params: Record<string, string>,
): { pathname: string; search: string } => {
  const search = new URLSearchParams()
  search.set(route.layer, route.name)
  Object.entries(route.paramMap ?? {}).forEach(([routeKey, subKey]) => {
    const value = params[routeKey]
    if (value !== undefined) {
      search.set(toLayerParamKey(route.layer, subKey), value)
    }
  })
  return { pathname: route.toPathname(params), search: `?${search.toString()}` }
}

export const deactivateOverlayLocation = (
  id: string,
  isBillPay: boolean,
): { pathname: string; search: string } =>
  overlayRedirectLocation(
    isBillPay
      ? deactivateBillPayCardOverlayRoute
      : deactivateVirtualCardOverlayRoute,
    { id },
  )
